import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Mail, Phone, Loader2, ArrowRight, AlertTriangle } from 'lucide-react';
import { sendFamilyOtp, sendFamilyPhoneOtp, splitMember } from '../../redux/familySlice';

// Giving a family member their own sign-in.
//
// NOT A MODAL. This renders INSIDE the Switch-profile panel, replacing the list
// while it runs — one window whose contents change, with Back returning to the
// list. It was a modal opened over that panel, and then a third alert over
// that; three layers to answer one question, each burying the one beneath it.
// Keeping this a plain component is what makes a single window possible: it
// owns no overlay and no dialog of its own.
//
// WHY VERIFY AT ALL. This creates a LOGIN, and a login is how medical records
// are reached. It has to land on contact details somebody has proven they hold,
// or the flow becomes a way to point an account at an address you picked.
//
// WHAT THE MOBILE STEP IS WORTH: the code is fixed at 123456 with no SMS
// provider connected, so it confirms the number was typed, not that anyone
// holds it. The EMAIL step is the real proof, which is why the server refuses
// without it whatever this screen does.
//
// VERIFYING AND LEAVING ARE TWO DECISIONS. The choose stage asks the second one
// and BOTH answers are real: staying still saves the contact details and still
// creates the sign-in. Cancelling used to discard proof the person had just
// given, leaving their profile with no email, no mobile and no way in.
// ACCESS is the stage that belongs to the STAY outcome, the way WARN belongs
// to the LEAVE one (owner, 2026-09-09). While this person had no sign-in the
// holder had to hold them at read & write — nobody else could act for them.
// Once they own a login that stops being true, so staying is now a SHARING
// decision and this asks what they share back. Leaving revokes the edge and
// asks nothing.
const STEP = { EMAIL: 'email', PHONE: 'phone', CHOOSE: 'choose', ACCESS: 'access', WARN: 'warn' };
const READ = 'read';
const READWRITE = 'readwrite';

export default function SplitMemberFlow({ member, onDone, onSplit, onChanged }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Someone who already has a login proved their contact when it was made.
    // Asking again would be theatre, and the server agrees — it only demands
    // proof when there is no login yet.
    const alreadyHasLogin = !!member?.hasLogin;

    const [step, setStep] = useState(alreadyHasLogin ? STEP.CHOOSE : STEP.EMAIL);
    const [email, setEmail] = useState(member?.email || '');
    const [emailOtp, setEmailOtp] = useState('');
    const [emailSent, setEmailSent] = useState(false);
    const [phone, setPhone] = useState(member?.phone || '');
    const [phoneOtp, setPhoneOtp] = useState('');
    const [phoneSent, setPhoneSent] = useState(false);
    const [busy, setBusy] = useState(false);
    // Defaults to full access: staying should change nothing on its own, and
    // narrowing is the deliberate choice (owner, 2026-09-09).
    const [permission, setPermission] = useState(READWRITE);

    if (!member) return null;

    const name = [member.name, member.surname].filter(Boolean).join(' ').trim();

    const fail = (err, fallback) => {
        const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
        toast.error(msg || fallback);
    };

    const requestEmailCode = async () => {
        if (!email.trim()) return toast.error(t('family.email_required', 'Enter an email address'));
        setBusy(true);
        try {
            await dispatch(sendFamilyOtp(email.trim())).unwrap();
            setEmailSent(true);
            toast.success(t('family.code_sent', 'We sent a code to that address'));
        } catch (err) {
            fail(err, t('family.code_failed', 'Could not send the code'));
        } finally { setBusy(false); }
    };

    const requestPhoneCode = async () => {
        if (!phone.trim()) return toast.error(t('family.phone_required', 'Enter a mobile number'));
        setBusy(true);
        try {
            await dispatch(sendFamilyPhoneOtp(phone.trim())).unwrap();
            setPhoneSent(true);
            toast.success(t('family.code_sent_sms', 'We sent a code to that number'));
        } catch (err) {
            fail(err, t('family.code_failed', 'Could not send the code'));
        } finally { setBusy(false); }
    };

    // ONE call for both outcomes. `keepInFamily` decides only whether they also
    // leave the group — the verification and the login are saved either way.
    const finish = async (keepInFamily) => {
        setBusy(true);
        try {
            const payload = alreadyHasLogin
                ? { patientId: member.patientId, keepInFamily, permission }
                : {
                    patientId: member.patientId, keepInFamily, permission,
                    email: email.trim(), emailOtp: emailOtp.trim(),
                    phone: phone.trim(), phoneOtp: phoneOtp.trim(),
                };
            const out = await dispatch(splitMember(payload)).unwrap();
            toast.success(out?.message?.en
                || (keepInFamily ? t('family.verified_kept', 'Verified. They remain in this family.')
                                 : t('family.split_done', 'They now have their own account')));
            if (!keepInFamily) onSplit?.(member.patientId);
            onChanged?.();
            onDone?.();
        } catch (err) {
            fail(err, t('family.split_failed', 'Could not complete that'));
        } finally { setBusy(false); }
    };

    const Stage = ({ n, label, active, done }) => (
        <div className="flex items-center gap-1.5">
            <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${
                done ? 'bg-[#4ABDBD] text-white'
                     : active ? 'bg-[#4ABDBD]/20 text-[#3AA8A8] ring-2 ring-[#4ABDBD]'
                              : 'bg-gray-100 text-gray-400'}`}>
                {done ? '✓' : n}
            </span>
            <span className={`text-[11px] ${active || done ? 'text-gray-700 font-semibold' : 'text-gray-400'}`}>
                {label}
            </span>
        </div>
    );

    const field = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-[#4ABDBD]';
    const btn = 'rounded-lg bg-[#4ABDBD] px-3 py-2 text-xs font-semibold text-white hover:bg-[#3AA8A8] disabled:opacity-50 cursor-pointer whitespace-nowrap';
    const onConfirmStage = step === STEP.CHOOSE || step === STEP.ACCESS || step === STEP.WARN;

    return (
        <>
            {!alreadyHasLogin && (
                <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-2.5">
                    <Stage n={1} label={t('family.stage_email', 'Email')}
                           active={step === STEP.EMAIL} done={step !== STEP.EMAIL} />
                    <ArrowRight className="h-3 w-3 text-gray-300" />
                    <Stage n={2} label={t('family.stage_mobile', 'Mobile')}
                           active={step === STEP.PHONE} done={onConfirmStage} />
                    <ArrowRight className="h-3 w-3 text-gray-300" />
                    <Stage n={3} label={t('family.stage_confirm', 'Confirm')}
                           active={onConfirmStage} done={false} />
                </div>
            )}

            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
                {step === STEP.EMAIL && (
                    <>
                        <p className="mb-3 text-xs text-gray-500">
                            {t('family.split_email_why',
                               'This becomes their sign-in, so the address has to be proven first.')}
                        </p>
                        <label className="mb-1 block text-xs font-semibold text-gray-600">
                            <Mail className="mr-1 inline h-3 w-3" />
                            {t('family.email', 'Email address')}
                        </label>
                        <div className="flex gap-2">
                            <input type="email" value={email} disabled={emailSent || busy}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="name@example.com" className={field} />
                            <button type="button" onClick={requestEmailCode} disabled={busy || emailSent} className={btn}>
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                                      : emailSent ? t('family.sent', 'Sent') : t('family.verify', 'Verify')}
                            </button>
                        </div>

                        {emailSent && (
                            <div className="mt-3">
                                <label className="mb-1 block text-xs font-semibold text-gray-600">
                                    {t('family.enter_code', 'Enter the 6-digit code')}
                                </label>
                                <div className="flex gap-2">
                                    <input value={emailOtp} maxLength={6} inputMode="numeric"
                                        onChange={(e) => setEmailOtp(e.target.value.replace(/\D/g, ''))}
                                        placeholder="------" className={`${field} tracking-[0.4em]`} />
                                    <button type="button" disabled={emailOtp.length !== 6}
                                        onClick={() => setStep(STEP.PHONE)} className={btn}>
                                        {t('family.next', 'Next')}
                                    </button>
                                </div>
                                <button type="button" onClick={() => { setEmailSent(false); setEmailOtp(''); }}
                                    className="mt-2 cursor-pointer text-[11px] text-gray-400 hover:underline">
                                    {t('family.change_email', 'Use a different address')}
                                </button>
                            </div>
                        )}
                    </>
                )}

                {step === STEP.PHONE && (
                    <>
                        <p className="mb-3 text-xs text-gray-500">
                            {t('family.split_phone_why', 'Now the mobile number for the account.')}
                        </p>
                        <label className="mb-1 block text-xs font-semibold text-gray-600">
                            <Phone className="mr-1 inline h-3 w-3" />
                            {t('family.mobile', 'Mobile number')}
                        </label>
                        <div className="flex gap-2">
                            <input value={phone} disabled={phoneSent || busy} inputMode="tel"
                                onChange={(e) => setPhone(e.target.value)}
                                placeholder="9884451234" className={field} />
                            <button type="button" onClick={requestPhoneCode} disabled={busy || phoneSent} className={btn}>
                                {busy ? <Loader2 className="h-3 w-3 animate-spin" />
                                      : phoneSent ? t('family.sent', 'Sent') : t('family.verify', 'Verify')}
                            </button>
                        </div>

                        {phoneSent && (
                            <div className="mt-3">
                                <label className="mb-1 block text-xs font-semibold text-gray-600">
                                    {t('family.enter_code', 'Enter the 6-digit code')}
                                </label>
                                <div className="flex gap-2">
                                    <input value={phoneOtp} maxLength={6} inputMode="numeric"
                                        onChange={(e) => setPhoneOtp(e.target.value.replace(/\D/g, ''))}
                                        placeholder="------" className={`${field} tracking-[0.4em]`} />
                                    <button type="button" disabled={phoneOtp.length !== 6}
                                        onClick={() => setStep(STEP.CHOOSE)} className={btn}>
                                        {t('family.next', 'Next')}
                                    </button>
                                </div>
                            </div>
                        )}

                        <button type="button" onClick={() => setStep(STEP.EMAIL)}
                            className="mt-3 cursor-pointer text-[11px] text-gray-400 hover:underline">
                            {t('family.back', 'Back')}
                        </button>
                    </>
                )}

                {/* The two outcomes, side by side and equally real. */}
                {step === STEP.CHOOSE && (
                    <>
                        <div className="rounded-xl bg-[#4ABDBD]/10 p-4">
                            <p className="text-sm font-semibold text-[#1a365d]">
                                {alreadyHasLogin
                                    ? t('family.split_ready_login', 'They already have their own sign-in.')
                                    : t('family.split_ready', 'Email and mobile are both verified.')}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">
                                {alreadyHasLogin
                                    ? t('family.choose_body_login', 'Should they stay in this family, or become a separate account?')
                                    : t('family.choose_body',
                                        'They can now sign in themselves. Should they stay in this family, or become a separate account?')}
                            </p>
                        </div>

                        <div className="mt-4 flex flex-col gap-2">
                            <button type="button" data-testid="keep-in-family" disabled={busy}
                                onClick={() => setStep(STEP.ACCESS)}
                                className="w-full cursor-pointer rounded-lg bg-[#4ABDBD] py-2.5 text-sm font-semibold text-white hover:bg-[#3AA8A8] disabled:opacity-50">
                                {t('family.stay_in_family', 'Stay in this family')}
                            </button>
                            <button type="button" data-testid="go-split" disabled={busy}
                                onClick={() => setStep(STEP.WARN)}
                                className="w-full cursor-pointer rounded-lg border border-red-200 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50">
                                {t('family.split_into_own', 'Split into their own account')}
                            </button>
                        </div>

                        {!alreadyHasLogin && (
                            <button type="button" onClick={() => setStep(STEP.PHONE)}
                                className="mt-3 w-full cursor-pointer text-[11px] text-gray-400 hover:underline">
                                {t('family.back', 'Back')}
                            </button>
                        )}
                    </>
                )}

                {/* Its own stage rather than sharing one with the choice.
                    Splitting cannot be undone from here — the primary loses
                    sight of those records and only a fresh invitation brings
                    them back — so it is not a button you can brush past. */}
                {/* STAYING, and what they share back from now on. */}
                {step === STEP.ACCESS && (
                    <>
                        <div className="rounded-xl bg-[#4ABDBD]/10 p-4">
                            <p className="text-sm font-semibold text-[#1a365d]">
                                {t('family.access_title', 'What may you do with their record?')}
                            </p>
                            <p className="mt-1 text-xs text-gray-600">
                                {t('family.access_body', 'They can sign in themselves now, so this is their account being shared with you. They can change this at any time from their own Sharing settings.')}
                            </p>
                        </div>

                        <div className="mt-4 flex flex-col gap-2">
                            {[
                                { id: READWRITE, title: t('family.access_rw', 'View and manage'), body: t('family.access_rw_body', 'Read the record and act on it — book, cancel, edit details. What you have today.') },
                                { id: READ, title: t('family.access_r', 'View only'), body: t('family.access_r_body', 'Read the record. Booking, cancelling and editing are theirs alone.') },
                            ].map((opt) => (
                                <button
                                    key={opt.id}
                                    type="button"
                                    data-testid={`split-permission-${opt.id}`}
                                    disabled={busy}
                                    onClick={() => setPermission(opt.id)}
                                    className={`w-full cursor-pointer rounded-xl border px-3 py-2.5 text-left transition-colors disabled:opacity-50 ${
                                        permission === opt.id
                                            ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 ring-1 ring-[#4ABDBD]'
                                            : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <span className="block text-sm font-semibold text-[#1a365d]">{opt.title}</span>
                                    <span className="mt-0.5 block text-[11px] leading-relaxed text-gray-500">{opt.body}</span>
                                </button>
                            ))}
                        </div>

                        <div className="mt-4 flex flex-col gap-2">
                            <button type="button" data-testid="split-keep-confirm" disabled={busy}
                                onClick={() => finish(true)}
                                className="w-full cursor-pointer rounded-lg bg-[#4ABDBD] py-2.5 text-sm font-semibold text-white hover:bg-[#3AA8A8] disabled:opacity-50">
                                {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                                      : t('family.access_confirm', 'Confirm')}
                            </button>
                            <button type="button" disabled={busy} onClick={() => setStep(STEP.CHOOSE)}
                                className="w-full cursor-pointer rounded-lg border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                {t('family.go_back', 'Go back')}
                            </button>
                        </div>
                    </>
                )}

                {step === STEP.WARN && (
                    <>
                        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
                            <p className="flex items-center gap-2 text-sm font-bold text-red-700">
                                <AlertTriangle className="h-4 w-4" />
                                {t('family.split_confirm_title', 'Split this account?')}
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-red-900">
                                <b>{name}</b>{' '}
                                {t('family.split_warn_body',
                                   'will become a separate account. You will no longer be able to see or manage their health records. Their records stay with them — nothing is deleted.')}
                            </p>
                        </div>

                        <div className="mt-4 flex flex-col gap-2">
                            <button type="button" data-testid="split-confirm" disabled={busy}
                                onClick={() => finish(false)}
                                className="w-full cursor-pointer rounded-lg bg-red-600 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                                {busy ? <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                                      : t('family.split_confirm_yes', 'Yes, split the account')}
                            </button>
                            <button type="button" disabled={busy} onClick={() => setStep(STEP.CHOOSE)}
                                className="w-full cursor-pointer rounded-lg border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                {t('family.split_confirm_no', 'Go back')}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </>
    );
}
